import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import {
  Container, H2, PageHero, Section, SectionHead, SectionHeader,
} from "../components/Primitives";
import { ProductGrid, ProductGridSkeleton } from "../components/ProductCard";
import { GrahaQuickNav, RashiQuickNav } from "../components/QuickNav";
import { getProducts } from "../lib/api";
import { useSEO } from "../lib/seo";
import { IMAGES, INTENT_IMAGE, PLANET_IMAGE } from "../content/images";
import { BUDGET_NAV, CATEGORY_NAV, INTENT_NAV, MUKHI_NAV, PLANET_NAV, RASHI_NAV, STONE_NAV } from "../content/nav";
import { EASE_OUT, Reveal, Stagger, StaggerItem, motion } from "../lib/motion";

const LADDER_IMAGE = {
  "/shop/gemstones": IMAGES["blue-sapphire"],
  "/shop/rudraksha": IMAGES.rudraksha,
  "/shop/malas": IMAGES.mala,
  "/shop/bracelets": IMAGES.mala,
  "/shop/pendants": IMAGES.pendant,
  "/shop/yantras": IMAGES.yantra,
  "/shop/new": IMAGES.emerald,
  "/shop/sold-archive": IMAGES.certificate,
};

const LADDER_NOTE = {
  "/shop/gemstones": "Nine navratna, each with a report on the laboratory's own domain",
  "/shop/rudraksha": "Nepal and Java, X-rayed above ₹3,000",
  "/shop/malas": "Hand-knotted, bead-matched, counted",
  "/shop/bracelets": "Measured to your wrist, never guessed",
  "/shop/pendants": "Sold unset, set only after you have seen the stone",
  "/shop/yantras": "Hand-engraved copper, not stamped",
  "/shop/new": "The most recent arrivals, newest first",
  "/shop/sold-archive": "Every stone we have ever sold, still verifiable",
};

function LadderTile({ item, big = false }) {
  return (
    <StaggerItem className={big ? "col-span-2 lg:row-span-2" : ""}>
      <Link
        to={item.to}
        data-testid={`ladder-${item.to.split("/").pop()}`}
        className={`group card-s elev relative flex h-full flex-col overflow-hidden transition-colors duration-300 hover:border-gold ${
          big ? "min-h-[300px] lg:min-h-[420px]" : ""
        }`}
      >
        <div className={`overflow-hidden bg-wash ${big ? "flex-1" : "aspect-[4/3]"}`}>
          <motion.img
            src={LADDER_IMAGE[item.to]}
            alt=""
            loading="lazy"
            className="dim-dark h-full w-full object-cover"
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.06 }}
            transition={{ duration: 0.95, ease: EASE_OUT }}
          />
        </div>
        <div className="flex items-end justify-between gap-4 p-5">
          <div>
            <span className={`block leading-tight text-deep transition-colors group-hover:text-gold ${big ? "text-xl sm:text-2xl" : "text-base"}`}>
              {item.label}
            </span>
            <span className="ui mt-1.5 block text-xs leading-snug text-dim">{LADDER_NOTE[item.to]}</span>
          </div>
          <ArrowUpRight className="h-4 w-4 shrink-0 text-dim transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold" />
        </div>
      </Link>
    </StaggerItem>
  );
}

export default function ShopLanding() {
  useSEO({
    title: "Shop Certified Gemstones, Rudraksha, Malas & Yantras",
    description:
      "Browse by category, by planet, by intent or by budget. Every gemstone carries a laboratory report you verify on the laboratory's own website, and every rudraksha above ₹3,000 publishes its X-ray.",
    canonical: "/shop",
  });

  const newest = useQuery({
    queryKey: ["products", "shop-landing"],
    queryFn: () => getProducts({ limit: 8, sort: "newest" }),
  });

  return (
    <>
      <PageHero
        eyebrow="Shop"
        lines={["Four ways in.", "All of them end at a", "report you can check."]}
        lede="Browse by category, by planet, by intent or by budget. Whichever route you take, every gemstone page shows species, origin, treatment, laboratory and report number, one honest price and the price per carat — and every rudraksha above ₹3,000 publishes its own X-ray."
      />

      <Section className="border-b border-rule">
        <Container>
          <SectionHead eyebrow="By category" title="Six categories and two archives" />
          <Stagger className="grid grid-cols-2 gap-4 sm:gap-5 lg:grid-cols-4" stagger={0.07}>
            {CATEGORY_NAV.map((c, i) => (
              <LadderTile key={c.to} item={c} big={i === 0} />
            ))}
          </Stagger>
        </Container>
      </Section>

      <Section className="border-b border-rule bg-surface">
        <Container>
          <SectionHeader
            eyebrow="By planet"
            title="The nine grahas and their principal stones"
          />
          <GrahaQuickNav />

          <SectionHeader
            eyebrow="By rashi"
            title="Twelve signs, and the stone traditionally read with each"
            className="!mt-24"
            lede="Rashi routes list the stones classically associated with the sign's ruling graha. They are a starting point for reading, not a recommendation — a chart is more than a sun sign, and we will say so on the call."
          />
          <RashiQuickNav />
        </Container>
      </Section>

      <Section className="border-b border-rule">
        <Container>
          <SectionHead eyebrow="By intent" title="What are you actually looking for?" />
          <Stagger className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7" stagger={0.055}>
            {INTENT_NAV.map((x) => (
              <StaggerItem key={x.slug}>
                <Link
                  to={`/shop/by-intent/${x.slug}`}
                  data-testid={`shop-intent-${x.slug}`}
                  className="group relative block overflow-hidden border border-rule"
                >
                  <motion.img
                    src={INTENT_IMAGE[x.slug]}
                    alt=""
                    loading="lazy"
                    className="dim-dark aspect-[3/4] w-full object-cover"
                    initial={{ scale: 1 }}
                    whileHover={{ scale: 1.07 }}
                    transition={{ duration: 0.95, ease: EASE_OUT }}
                  />
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/90 via-ink/40 to-transparent p-3.5 pt-10">
                    <span className="ui block text-xs font-medium leading-snug text-white">{x.label}</span>
                  </span>
                  <span className="pointer-events-none absolute inset-0 border border-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                </Link>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-24">
            <H2>By budget</H2>
          </Reveal>
          <Stagger className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" stagger={0.07}>
            {BUDGET_NAV.map((b) => (
              <StaggerItem key={b.slug}>
                <Link
                  to={`/shop/by-budget/${b.slug}`}
                  data-testid={`shop-budget-${b.slug}`}
                  className="card-s elev group flex h-full flex-col p-7 transition-colors duration-300 hover:border-gold"
                >
                  <p className="num text-lg text-deep transition-colors group-hover:text-gold">{b.label}</p>
                  <p className="ui mt-2.5 text-xs leading-[1.7] text-dim">
                    Inclusive of tax. No hidden additions at checkout.
                  </p>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal className="mt-24">
            <H2>Gemstones by species</H2>
            <div className="mt-8 flex flex-wrap gap-2">
              {STONE_NAV.map((s) => (
                <Link
                  key={s.slug}
                  to={`/shop/gemstones/${s.slug}`}
                  className="ui border border-rule px-3.5 py-2 text-xs text-dim transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  {s.name} <span className="opacity-60">({s.hindi})</span>
                </Link>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-24" delay={0.06}>
            <H2>Rudraksha by mukhi</H2>
            <div className="mt-8 flex flex-wrap gap-2">
              {MUKHI_NAV.map((m) => (
                <Link
                  key={m}
                  to={`/shop/rudraksha/${m}-mukhi`}
                  className="num border border-rule px-4 py-2 text-sm text-dim transition-colors duration-300 hover:border-gold hover:text-gold"
                >
                  {m} mukhi
                </Link>
              ))}
              <Link to="/shop/rudraksha/nepal" className="ui border border-rule px-4 py-2 text-sm text-dim transition-colors duration-300 hover:border-gold hover:text-gold">
                Nepal
              </Link>
              <Link to="/shop/rudraksha/java" className="ui border border-rule px-4 py-2 text-sm text-dim transition-colors duration-300 hover:border-gold hover:text-gold">
                Java
              </Link>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Section className="bg-surface">
        <Container>
          <SectionHead
            eyebrow="Recently listed"
            title="The newest arrivals, newest first"
            action={<Link to="/shop/new" className="link-u ui text-sm">All new arrivals →</Link>}
          />
          {newest.isLoading ? (
            <ProductGridSkeleton count={6} testId="shop-landing-skeleton" />
          ) : (newest.data?.items || []).length ? (
            <ProductGrid products={newest.data?.items || []} testId="shop-landing-grid" />
          ) : (
            <p className="ui text-sm leading-[1.7] text-dim" data-testid="shop-landing-empty">
              Nothing has been listed this week — every piece that has passed through is in the{" "}
              <Link to="/shop/sold-archive" className="link-u">sold archive</Link>, certificates intact.
            </p>
          )}
        </Container>
      </Section>
    </>
  );
}
